// Текст сокращён многоточием (README «Доступность»; SC-007): ширина содержимого больше видимой.
// Проверяется после отрисовки и при каждой смене размера (ResizeObserver): поле сузилось вместе
// с окном — строка могла перестать помещаться, расширилось — снова помещается. После смены
// размера строка перемеряется в следующем кадре, а не в обработчике ResizeObserver: открытый
// по результату слой (FieldLine) сам наблюдается ResizeObserver, и новое наблюдение посреди
// обхода дало бы ошибку «ResizeObserver loop completed with undelivered notifications».
import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/** Содержимое шире видимой части; 1 px — запас на округление дробной ширины. */
export function isTruncated(element: Element): boolean {
  return element.scrollWidth > element.clientWidth + 1;
}

/**
 * Текст, который видно на экране: без скрытой строки диктора (kp-sr-only) — например, номера
 * операции группами через пробел рядом с видимым номером с дефисами (T167).
 */
export function visibleText(element: Element): string {
  let text = '';
  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? '';
    else if (node instanceof Element && !node.classList.contains('kp-sr-only')) {
      text += visibleText(node);
    }
  }
  return text;
}

export interface Truncation {
  truncated: boolean;
  /** Полный видимый текст строки, пока она сокращена; иначе ''. */
  text: string;
  /**
   * Ширина строки по раскладке (clientWidth), пока она сокращена; иначе 0. Не зависит
   * от transform: окно, которое въезжает с масштабом, не меняет её на каждом кадре.
   */
  width: number;
}

const FITS: Truncation = { truncated: false, text: '', width: 0 };

/**
 * Сокращена ли строка ref и какой у неё полный текст. content — то, что в строке: при его смене
 * строка перемеряется.
 */
export function useTruncation(ref: RefObject<Element | null>, content: unknown): Truncation {
  const [state, setState] = useState<Truncation>(FITS);
  useLayoutEffect(() => {
    const element = ref.current;
    const measure = () => {
      const next: Truncation =
        element && isTruncated(element)
          ? { truncated: true, text: visibleText(element), width: element.clientWidth }
          : FITS;
      setState((current) =>
        current.truncated === next.truncated &&
        current.text === next.text &&
        current.width === next.width
          ? current
          : next,
      );
    };
    measure();
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [ref, content]);
  return state;
}
